import { useCallback, useEffect, useMemo, useRef, useState, } from "react"
import {useNavigate} from 'react-router';
import { NavLink } from "react-router-dom";
import API from "../service/api";
import { useTranslation } from "react-i18next";
import Lang from "../service/lang";
import { useWindowSize } from "../hooks/useWindowSize";
import { MDBInput, MDBPopover, MDBTypography } from "mdb-react-ui-kit";
import { host } from "../service/host";
import { CustomPopover } from "./popover/CustomPopover";


const CountryByLanguage = new Map<string, string>();
CountryByLanguage.set('en', 'gb');

export interface HeaderProps {
    userName: string;
    selectedGroupId: number|null;
    onQR: () => void;
    onHome: () => void;
    onHelp: () => void;

}

export const Header = ({userName, onQR, onHome, onHelp, selectedGroupId}: HeaderProps) => {
    const [mobileSearch, setMobileSearch] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [notFound, setNotFound] = useState(false);
    const navigate = useNavigate();

    const search = useCallback((text?: string) => {
        API.POST('/api/groups/search', {text: text||searchText})
            .then(data => {
                if (data.length == 0) setNotFound(true);
                if (data.length > 0) {
                    const { groupId, locationId, machineId } = data[0];
                    let uri = `../group/${groupId}`;
                    if (locationId) uri += `/locations/${locationId}`;
                    if (machineId) uri += `/machines/${machineId}`;
                    navigate(uri);
                }
            })
            .catch(err => {
                console.error(err);
            });
    }, [searchText]);

    return (
        <nav className="navbar navbar-expand-lg navbar-light bg-light p-0 w-100">
            {!mobileSearch &&
                <MainHeader
                    userName={userName}
                    onMobileSearch={() => setMobileSearch(true)}
                    selectedGroupId={selectedGroupId}
                    onQR={onQR}
                    onHome={onHome}
                    onHelp={onHelp}
                    onSearch={search}
                />
            }
            {mobileSearch &&
                <div className="d-flex flex-row w-100 py-1">
                    <div className="input-group form-outline d-flex flex-fill w-auto ms-2" style={{height: 35}}>
                        <MDBInput
                            type="search"
                            className={notFound ? ' is-invalid' : ''}
                            value={searchText}
                            onInput={ev => { setSearchText(ev.currentTarget.value); setNotFound(false); }}
                            onKeyDown={ev => {
                                if (ev.key == 'Enter') { ev.preventDefault(); ev.stopPropagation(); search(); }
                            }} autoComplete="off" 
                        />
                        <button type="button" className="btn btn-primary" onClick={() => search()}>
                            <i className="fas fa-search"></i>
                        </button>
                    </div>
                    <span role="button" className="px-3 d-flex justify-content-center align-items-center" onClick={() => setMobileSearch(false)}><i className="fas fa-x fa-lg"/></span>
                </div>
            }
			
		</nav>
    )
}

const MainHeader = ({userName, onMobileSearch, selectedGroupId, onQR, onHome, onHelp, onSearch}: {
    userName: string;
    selectedGroupId: number|null;
    onMobileSearch: () => void;
    onQR: () => void;
    onHome: () => void;
    onHelp: () => void;
    onSearch: (text: string) => void;
}) => {
    const [searchText, setSearchText] = useState('');
    const [notFound, setNotFound] = useState(false);
    const [lang, setLang] = useState<string>('');
    const [languages, setLanguages] = useState([]);
    const { t } = useTranslation();
    const { innerWidth } = useWindowSize();
    const mobile = useMemo(() => innerWidth < 800, [innerWidth]);

    const getUserName = useCallback((name: string) => {
        const n = name.split(' ')[0];
        return n.length <= 8 ? n : (n.slice(0, 7)+'...');
    }, []);

    useEffect(() => {
        setLang(Lang.instance.getCurrent());
        const langSetter = (l: string) => setLang(l);
        Lang.instance.addListener(langSetter);

        return () => {
            Lang.instance.removeListener(langSetter);
        }
        
    }, []);

    useEffect(() => {
        setLanguages(Lang.instance.getAll().filter((l: string) => l !== lang));
    }, [lang]);

    return (
        <div className="container-fluid justify-content-between">
            <NavLink to={selectedGroupId ? `/group/${selectedGroupId}` : '/'}>
                <span role="button" onClick={onHome} className="nav-link d-flex align-items-center p-1 user-select-none cursor-pointer" style={{cursor: 'pointer'}}>
                    <div className="d-flex align-items-center pe-1">
                        <img src={`/logo.png`} style={{width: 28, height: 28}}/>
                        <MDBTypography color="primary" className="h5 fw-bold mb-0 ms-1" style={{textShadow: '1px 1px 1px rgba(0,0,0,0.1)'}}>{host.getTitle()}</MDBTypography>
                    </div>
                </span>
            </NavLink>
            
            <span className="d-flex flex-fill"></span>
            {!mobile &&
                <div className="container-fluid justify-content-between d-flex flex-row align-items-center" style={{width: 300}}>
                    { (window.location.protocol === 'https:' || window.location.hostname === 'localhost') && <span role="button" className="me-2" onClick={() => onQR()}><i className="fas fa-camera"/></span> }
                    
                    <div className="input-group form-outline" style={{height: 30}}>
                        <MDBInput
                            type="search"
                            className={notFound ? 'is-invalid':''}
                            style={{minHeight: 1, height: 30}}
                            value={searchText}
                            onChange={ev => { setSearchText(ev.currentTarget.value); setNotFound(false); }}
                            onKeyDown={ev => {
                                if (ev.key == 'Enter') { ev.preventDefault(); ev.stopPropagation(); onSearch(searchText); }
                            }} autoComplete="off" 
                        />
                        <button type="button" className="btn btn-primary" onClick={() => onSearch(searchText)}>
                            <i className="fas fa-search"></i>
                        </button>
                    </div>
                    
                </div>
            }
            {mobile && <div>
                { (window.location.protocol === 'https:' || window.location.hostname === 'localhost') && <span role="button" className="me-3" onClick={() => onQR()}><i className="fas fa-camera fa-lg"/></span> }
                    <span role="button" className="me-3" onClick={() => onMobileSearch()}><i className="fas fa-magnifying-glass fa-lg"/></span>
                </div>
            }

            <CustomPopover
                trigger={(
                    <div className="d-flex flex-row align-items-center">
                        <span className={" flag-icon flag-icon-"+((CountryByLanguage.get(lang))||lang)} style={{width: 21, height: 16}}/>
                        <span className="me-1"><i className="fas fa-caret-down"/></span>
                    </div>
                )}
                placement="bottom"
            >
                {close => (
                    <div>
                        {languages.map(l => (
                            <div key={l} className="custom-select-item" onClick={() => {close(); Lang.instance.changeLanguage(l); }}>
                                <span className={"me-1 flag-icon flag-icon-"+(CountryByLanguage.get(l)||l)}/>
                                <span style={{overflow: 'hidden', textOverflow: 'ellipsis'}}>{t('language_title.'+l)}</span>
                            </div>
                        ))}
                    </div>
                )}
            </CustomPopover>

            <span role="button" className="me-2" onClick={() => onHelp()}><i className="fas fa-circle-question text-primary fa-lg"/></span>

            <NavLink to="/profile" className={({isActive}) => "nav-item ps-2 user-select-none  " + (isActive ? 'border-bottom border-2 border-primary rounded-top text-primary' : 'border-start border-1 text-dark')} style={{cursor: 'pointer'}}>
                <span className="nav-link d-flex align-items-center user-select-none">
                    <span><i className="fas fa-user-circle fa-xl"></i></span>
                    {!mobile && <strong className="ms-1">{getUserName(userName)}</strong>}
                </span>
            </NavLink>
        </div>
    );
};